import { useApplicationState } from './App.state';
import { useEffect, useState } from 'react';
import { Spinner } from '@vwfs-bronson/bronson-react';
import { SaveAndContinue } from './components/SaveAndContinue/SaveAndContinue';
import { getTransactionData } from './api/transaction';
import { InteractionManagementStatusType, TransactionStatusType } from '@bus/models';
import { mapBffDealerToUiDealer } from '../mappers';
import { RouterHelpers } from './router/router-helpers';
import { DataStorageData } from '../shared/types';
import { useNavigate } from 'react-router-dom';
import axios from './api/axios';
import { Step } from '../shared/interface';
import { mapDataStorageDataToFormikState, mapToJourneyStep } from './utils/mappers';
import { queryClient } from './queryClient';
import { transactionDataQueryKey } from './api/transactionQueryKeys';
import { getSaveForLaterStepFromPath } from './utils/saveForLater';
import { setSearchParams } from './utils/set-search-params';

const dynamicData = { sessionToken: '', transactionId: '' };

const stepOrder: Record<Step, number> = {
  [Step.MM1]: 1,
  [Step.MM2]: 2,
  [Step.DEALER_SELECTION]: 3,
  [Step.CONTACT_DETAILS]: 4,
  [Step.FINAL_CONFIRMATION]: 5
};

const getPathnameFromUrl = (url?: string): string | undefined => {
  if (!url) {
    return undefined;
  }

  return new URL(url, window.location.href).pathname;
};

const isSaveForLaterUrlUpToDate = (
  saveForLaterStep: Step | undefined,
  journeyStep: Step | null
): boolean => {
  if (!saveForLaterStep || !journeyStep) {
    return true;
  }

  return stepOrder[saveForLaterStep] >= stepOrder[journeyStep];
};

axios.interceptors.request.use((config) => {
  if (dynamicData.sessionToken) {
    config.headers['x-auth-token'] = dynamicData.sessionToken;
  }
  if (dynamicData.transactionId) {
    config.headers['x-transaction-id'] = dynamicData.transactionId;
  }
  return config;
});

const WithDataLoaded = ({ children }: { children: JSX.Element }): JSX.Element => {
  const navigate = useNavigate();
  const { state, dispatch } = useApplicationState();
  const shouldRestoreInitialState = Boolean(state.transactionId && state.sessionToken);
  const [isLoading, setIsLoading] = useState(shouldRestoreInitialState);
  const [dataLoaded, setDataLoaded] = useState(shouldRestoreInitialState);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (state.sessionToken) {
      dynamicData.sessionToken = state.sessionToken;
    }
  }, [state.sessionToken]);

  useEffect(() => {
    if (state.transactionId) {
      dynamicData.transactionId = state.transactionId;
    }
  }, [state.transactionId]);

  useEffect(() => {
    if (dataLoaded && state.sessionToken && state.transactionId) {
      handleApplicationState();
    }
  }, [dataLoaded, state.sessionToken, state.transactionId]);

  const handleApplicationState = () => {
    setIsLoading(true);
    getTransactionData(state.transactionId).then((dataStorageData: DataStorageData) => {
      queryClient.setQueryData(transactionDataQueryKey(state.transactionId), dataStorageData);

      const transactionStatus = dataStorageData.transaction.status;
      const dealer = dataStorageData.dealerData
        ? mapBffDealerToUiDealer(dataStorageData.dealerData)
        : undefined;

      if (transactionStatus === TransactionStatusType.COMPLETED) {
        dispatch({
          type: 'updateState',
          state: {
            ...state,
            transactionStatus,
            dealer
          }
        });
      } else {
        dispatch({
          type: 'updateState',
          state: {
            ...state,
            transactionStatus,
            dealer,
            ...mapDataStorageDataToFormikState(dataStorageData)
          }
        });
      }

      const journeyStep = mapToJourneyStep(
        dataStorageData.transaction.interactionManagementStatus as InteractionManagementStatusType
      );

      const saveForLater = dataStorageData?.custom?.saveForLater;
      const saveForLaterUrl = getPathnameFromUrl(saveForLater?.url);
      const saveForLaterStep = saveForLater?.currentStep ?? getSaveForLaterStepFromPath(
        getPathnameFromUrl(saveForLater?.url)
      );

      if (
        transactionStatus !== TransactionStatusType.COMPLETED
        && saveForLaterUrl
        && isSaveForLaterUrlUpToDate(saveForLaterStep, journeyStep)
      ) {
        navigate(saveForLaterUrl, { replace: true });
        setIsLoading(false);
        return;
      }

      switch (
        journeyStep // Navigate to one step ahead of the already completed step
      ) {
        case Step.MM1:
          navigate(RouterHelpers.getMatchMakerQuestionPage2Path(), { replace: true });
          break;
        case Step.MM2:
          navigate(RouterHelpers.getDealerSelectionPagePath(), { replace: true });
          break;
        case Step.DEALER_SELECTION:
          navigate(RouterHelpers.getPersonalDetailsPath(), { replace: true });
          break;
        case Step.CONTACT_DETAILS:
          navigate(RouterHelpers.getSummaryPagePath(), { replace: true });
          break;
        case Step.FINAL_CONFIRMATION:
          navigate(RouterHelpers.getConfirmationPagePath(), { replace: true });
          break;
        default:
          break;
      }

      setIsLoading(false);
    }).catch((e) => {
      const status = e?.response?.status || 500;

      if (status === 403) {
        setSearchParams({ token: null });
        dispatch({ type: 'updateSessionToken', sessionToken: '' });
      }
    });
  };

  return (
    <>
      {!state?.sessionToken && !error && (
        <SaveAndContinue
          onAuthorizeComplete={() => setDataLoaded(true)}
          onError={() => setError(true)}
        />
      )}

      {isLoading && <Spinner testId="with-data-loaded-spinner" fullPage />}

      {((!isLoading && state?.transactionId && state?.sessionToken) || error) && children}
    </>
  );
};

export default WithDataLoaded;
